import httpClient from "../httpClient";

export interface Audience {
  id: string;
  name: string;
  userCount: number;
  checked: boolean;
  role?: number;
  department?: string;
}

export interface NotificationRecord {
  id: string;
  uuid?: string;
  title: string;
  subtitle: string;
  type: 'email' | 'in-app' | 'both';
  audiences: string[];
  status: 'sent' | 'scheduled' | 'draft' | 'sending' | 'failed';
  date: string;
  dateLabel: string;
  openRate?: number;
  clickRate?: number;
  message: string;
  targetAudiences: Array<{ name: string; color: string }>;
  sentDate: string;
}

export interface CampaignData {
  title: string;
  subject: string;
  message: string;
  targetAudience: {
    roles?: number[];
    departments?: string[];
    specificUsers?: string[];
  };
  deliveryMethod: 'email' | 'in-app' | 'both';
  scheduledFor?: string | null;
}

const ENDPOINTS = {
  CAMPAIGNS: "/admin/marketing/campaigns",
  SEND_CAMPAIGN: (uuid: string) => `/admin/marketing/campaigns/${uuid}/send`,
};

// Mock audience list - used for UI dropdown selection
const mockAudiences: Audience[] = [
  { id: '1', name: 'All Trainees', userCount: 150, checked: false, role: 1 },
  { id: '2', name: 'All Instructors', userCount: 25, checked: false, role: 2 },
  { id: '3', name: 'All Admins', userCount: 5, checked: false, role: 3 },
];

export const getAudiences = async (): Promise<{
  success: boolean;
  data?: Audience[];
  error?: { message: string } | null;
}> => {
  try {
    return {
      success: true,
      data: mockAudiences,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch audiences' }
    };
  }
};

export const getNotificationHistory = async (): Promise<{
  success: boolean;
  data?: NotificationRecord[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<any>(ENDPOINTS.CAMPAIGNS);

    if (response.success && response.data) {
      const records: NotificationRecord[] = response.data.map((campaign: any) => ({
        id: campaign.id?.toString() || campaign.uuid,
        uuid: campaign.uuid,
        title: campaign.title,
        subtitle: campaign.message?.substring(0, 30) + '...',
        type: campaign.delivery_method || campaign.deliveryMethod || 'both',
        audiences: campaign.target_audience?.roles?.map((r: number) =>
          r === 1 ? 'Trainees' : r === 2 ? 'Instructors' : 'Admins'
        ) || [],
        status: campaign.status,
        date: campaign.sent_at || campaign.scheduled_for || campaign.created_at,
        dateLabel: campaign.status === 'sent' ? 'Sent' :
                   campaign.status === 'scheduled' ? 'Scheduled' : 'Draft',
        openRate: campaign.total_opened && campaign.total_sent ?
                  Math.round((campaign.total_opened / campaign.total_sent) * 100) : undefined,
        clickRate: campaign.total_clicked && campaign.total_sent ?
                   Math.round((campaign.total_clicked / campaign.total_sent) * 100) : undefined,
        message: campaign.message,
        targetAudiences: campaign.target_audience?.roles?.map((r: number) => ({
          name: r === 1 ? 'Trainees' : r === 2 ? 'Instructors' : 'Admins',
          color: '#f59e0b'
        })) || [],
        sentDate: campaign.sent_at || ''
      }));

      return { success: true, data: records, error: null };
    }

    return { success: false, data: [], error: { message: response.error?.message || 'Failed to fetch campaigns' } };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch notification history' } };
  }
};

export const createCampaign = async (campaignData: CampaignData): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.CAMPAIGNS, campaignData);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to create campaign' } };
  }
};

export const sendCampaign = async (uuid: string): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.SEND_CAMPAIGN(uuid), {});
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to send campaign' } };
  }
};

export const updateCampaign = async (uuid: string, data: Partial<CampaignData>): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<any>(`${ENDPOINTS.CAMPAIGNS}/${uuid}`, data);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to update campaign' } };
  }
};

export const deleteCampaign = async (uuid: string): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<any>(`${ENDPOINTS.CAMPAIGNS}/${uuid}`);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to delete campaign' } };
  }
};
